/*
 * CraftBarn migration quantity-offer cart bridge.
 *
 * Scope: imported product forms that have a mapped numeric Shopify variant ID.
 * It does not alter checkout, patch window.fetch, create discounts, or replace the theme cart.
 * The parent process supplies window.CBMQuantityRuntimeMap after the full variant export is joined.
 */
(() => {
  'use strict';

  if (window.CBMOfferCart && window.CBMOfferCart.version) return;

  const USD = 'USD';
  const MIGRATION_CODE_PREFIX = 'CBM-Q3-';
  const BUNDLE_SHIPPING_CODE = 'CBM-BUNDLE-SHIPPING';
  const busyForms = new WeakSet();
  const wiredForms = new WeakSet();
  const pendingFormStates = new WeakMap();
  const parsedProductContexts = new Map();
  // Every CBM cart write and code reconciliation runs in one FIFO queue. Plans are made
  // only while their task owns the queue, so an initial read or earlier interaction cannot
  // write a discount plan computed against a subsequently changed cart.
  let cartQueue = Promise.resolve();
  let cartTransactionSequence = 0;

  function queueCartTransaction(label, work) {
    const sequence = ++cartTransactionSequence;
    const run = () => work({ label, sequence });
    const result = cartQueue.then(run, run);
    // Keep the queue usable after a recoverable cart request failure. The initiating UI owns
    // user-visible reporting; later, independent cart actions must still be able to proceed.
    cartQueue = result.catch(() => null);
    return result;
  }

  function own(object, key) {
    return Object.prototype.hasOwnProperty.call(object || {}, key);
  }

  function asPositiveInteger(value, fallback) {
    const number = Number.parseInt(String(value == null ? '' : value), 10);
    return Number.isInteger(number) && number > 0 ? number : fallback;
  }

  function asCents(value) {
    const number = Number(value);
    return Number.isInteger(number) && number >= 0 ? number : null;
  }

  function shopRoot() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === 'string' && root.length ? root.replace(/\/?$/, '/') : '/';
  }

  function cartUrl(path) {
    return `${shopRoot()}${String(path || '').replace(/^\//, '')}`;
  }

  function mapForVariant(variantId) {
    const map = window.CBMQuantityRuntimeMap;
    const raw = map && map[String(variantId)];
    const q1 = raw && asCents(raw.q1_total_cents);
    return q1 === null || !raw ? null : { q1_total_cents: q1 };
  }





  function selectorForForm(form) {
    if (!form) return null;
    if (form.dataset.cbmOfferSelector) return document.getElementById(form.dataset.cbmOfferSelector);
    if (form.id) {
      const selectors = Array.from(document.querySelectorAll('[data-cbm-offer-selector][data-cbm-form-id]'));
      const selector = selectors.find((node) => node.dataset.cbmFormId === form.id);
      if (selector) return selector;
    }
    if (form.id) {
      const nativeBreaks = Array.from(document.querySelectorAll('quantity-breaks[data-cbm-offer-selector]'));
      const selector = nativeBreaks.find((node) => Array.from(node.querySelectorAll('input[name="quantity"]')).some((input) => input.form === form));
      if (selector) return selector;
    }
    if (form.id) {
      const nativeBreaks = Array.from(document.querySelectorAll('quantity-breaks'));
      return nativeBreaks.find((node) => Array.from(node.querySelectorAll('input[name="quantity"]')).some((input) => input.form === form)) || null;
    }
    return form.closest('[data-cbm-offer-selector]');
  }

  function contextIdForForm(form) {
    const selector = selectorForForm(form);
    return form.dataset.cbmOfferProductJson || (selector && selector.dataset.cbmProductJson) || '';
  }

  function productContextForForm(form) {
    const id = contextIdForForm(form);
    if (!id) return null;
    if (parsedProductContexts.has(id)) return parsedProductContexts.get(id);

    const script = document.getElementById(id);
    if (!script) return null;
    try {
      const parsed = JSON.parse(script.textContent || '{}');
      const valid = parsed && typeof parsed === 'object' && parsed.variants && typeof parsed.variants === 'object'
        ? parsed
        : null;
      parsedProductContexts.set(id, valid);
      return valid;
    } catch (error) {
      parsedProductContexts.set(id, null);
      return null;
    }
  }

  function marketCurrencyForForm(form) {
    const context = productContextForForm(form);
    const support = document.querySelector('[data-cbm-currency]');
    const currency = (context && context.currency) || (support && support.dataset.cbmCurrency) || USD;
    return typeof currency === 'string' ? currency.toUpperCase() : USD;
  }

  function presentmentPriceForVariant(form, variantId) {
    const context = productContextForForm(form);
    if (!context || !context.variants) return null;
    const variant = context.variants[String(variantId)];
    return variant && asCents(variant.price_cents);
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (error) {
      return `${cents} ${currency}`;
    }
  }

  function formDataFor(form) {
    // This deliberately includes controls outside the form that use form="...".
    return new FormData(form);
  }

  function selectedVariantId(form, formData) {
    const data = formData || formDataFor(form);
    const value = data.get('id');
    return value == null ? '' : String(value);
  }

  function selectedQuantity(form, formData) {
    const data = formData || formDataFor(form);
    return asPositiveInteger(data.get('quantity'), 1);
  }

  function nativeQuantityBreaksForForm(form) {
    const selector = selectorForForm(form);
    if (selector && selector.matches && selector.matches('quantity-breaks')) return selector;
    if (!form) return null;
    return Array.from(document.querySelectorAll('quantity-breaks')).find((node) =>
      Array.from(node.querySelectorAll('input[name="quantity"]')).some((input) => input.form === form)
    ) || null;
  }

  function selectedVariantIdFromRuntime(value) {
    if (typeof value === 'number' || typeof value === 'string') return String(value);
    if (!value || typeof value !== 'object') return '';
    const id = value.id == null ? value.variant_id == null ? value.variantId : value.variant_id : value.id;
    return id == null ? '' : String(id);
  }

  function selectedVariantIdsForForm(form, data) {
    const quantity = selectedQuantity(form, data);
    const selectedId = selectedVariantId(form, data);
    if (quantity === 1) return { ids: selectedId ? [selectedId] : [], exact: Boolean(selectedId) };

    const quantityBreaks = nativeQuantityBreaksForForm(form);
    if (!quantityBreaks) {
      // The legacy one-variant selector has no independent rows. Preserve its native quantity path.
      return { ids: selectedId ? Array(quantity).fill(selectedId) : [], exact: Boolean(selectedId) };
    }

    const active = Array.from(quantityBreaks.querySelectorAll('input[name="quantity"]'))
      .find((input) => input.checked && asPositiveInteger(input.value, 0) === quantity);
    // Shrine's quantity-breaks element owns the resolved, availability-aware selected variants.
    // Use it rather than multiplying the main-form id: Get 2/Get 3 rows may intentionally mix
    // colors or sizes. Some versions store an active-row array, while the captured live contract
    // stores every row by data-input and requires reading only the checked row's exact array.
    const runtime = quantityBreaks.selectedVariants;
    const runtimeValues = Array.isArray(runtime)
      ? runtime
      : runtime && typeof runtime === 'object' && active && active.dataset.input && Array.isArray(runtime[active.dataset.input])
        ? runtime[active.dataset.input]
        : [];
    const runtimeSelection = runtimeValues.map(selectedVariantIdFromRuntime).filter(Boolean);
    if (runtimeSelection.length === quantity) return { ids: runtimeSelection, exact: true };

    // The same native element writes each resolved result back to the selector row. This is a
    // compatibility read only; it never guesses an option combination or revives a sold-out row.
    const label = active && active.id
      ? Array.from(quantityBreaks.querySelectorAll('label[for]')).find((node) => node.htmlFor === active.id)
      : null;
    const rowSelection = label
      ? Array.from(label.querySelectorAll('.quantity-break__selector-item[data-selected-id]'))
        .map((row) => String(row.dataset.selectedId || '')).filter(Boolean)
      : [];
    if (rowSelection.length === quantity) return { ids: rowSelection, exact: true };

    return { ids: [], exact: false };
  }

  function mapEntriesForVariantIds(ids) {
    const entries = ids.map((id) => mapForVariant(id));
    return entries.length && entries.every(Boolean) ? entries : null;
  }



  function nativeTileLabel(selector, tile) {
    if (!selector || !tile || !tile.id) return null;
    return Array.from(selector.querySelectorAll('label[for]')).find((label) => label.htmlFor === tile.id) || null;
  }

  function isCartAddForm(form) {
    if (!(form instanceof HTMLFormElement) || !form.action) return false;
    try {
      return new URL(form.action, window.location.href).pathname.includes('/cart/add');
    } catch (error) {
      return false;
    }
  }

  function hasImportedProductMarker() {
    const support = document.querySelector('[data-cbm-source-product-id]');
    return Boolean(support && support.dataset.cbmSourceProductId);
  }

  function isImportedMappedForm(form, data) {
    if (!isCartAddForm(form) || !hasImportedProductMarker()) return false;
    return Boolean(mapForVariant(selectedVariantId(form, data)));
  }

  function offerPriceSet(form, entry, variantId) {
    const currency = marketCurrencyForForm(form);
    const presentment = presentmentPriceForVariant(form, variantId);
    const unit = presentment === null && currency === USD ? entry.q1_total_cents : presentment;
    if (unit === null) return null;
    const config = window.CBMOfferCartConfig;
    return { currency, canApplyAutomatic: Boolean(config && config.automaticProductDiscountVerified),
      q1: unit, q2: unit * 2, q3: null };
  }

  function offerTiles(selector) {
    return Array.from(selector.querySelectorAll('[data-cbm-offer-tier]')).filter((tile) =>
      !tile.matches || !tile.matches('label.quantity-break')
    );
  }

  function priceNodeForTile(tile, selector) {
    const nested = tile.querySelector('[data-cbm-offer-price]');
    if (nested) return nested;
    const label = nativeTileLabel(selector, tile);
    return label && label.querySelector('[data-cbm-offer-price]');
  }

  function prepareNativeSelector(selector) {
    if (!selector || !selector.matches || !selector.matches('quantity-breaks')) return;
    Array.from(selector.querySelectorAll('input[name="quantity"][type="radio"]')).forEach((input) => {
      const tier = asPositiveInteger(input.value, 0);
      if (!tier) return;
      input.dataset.cbmOfferTier = String(tier);
      const label = nativeTileLabel(selector, input);
      if (!label) return;
      label.dataset.cbmOfferTier = String(tier);
      const price = label.querySelector('.quantity-break__price');
      if (price) price.setAttribute('data-cbm-offer-price', '');
    });
  }

  function activeSelectionPrice(form, data, entry) {
    const selection = selectedVariantIdsForForm(form, data);
    const quantity = selectedQuantity(form, data);
    const entries = selection.exact ? mapEntriesForVariantIds(selection.ids) : null;
    if (!entries) return { selection, entries: null, cents: null };
    const currency = marketCurrencyForForm(form);
    const prices = selection.ids.map((id, index) => {
      const native = presentmentPriceForVariant(form, id);
      return native === null && currency === USD ? entries[index].q1_total_cents : native;
    });
    if (prices.some((value) => value === null)) return { selection, entries, cents: null };
    // Shopify owns automatic percentage allocations and market rounding. Never revive old
    // fixed-cent totals or promise an exact bundle amount before the cart is calculated.
    return { selection, entries, cents: quantity >= 3 ? null : prices.reduce((sum, value) => sum + value, 0) };
  }

  function writeStatus(form, message, state) {
    const selector = selectorForForm(form);
    let node = selector && selector.querySelector('[data-cbm-offer-status]');
    if (!node && form.id) node = document.querySelector(`[data-cbm-offer-status][data-cbm-form-id="${form.id}"]`);
    if (!node && selector && selector.matches && selector.matches('quantity-breaks')) {
      node = document.createElement('p');
      node.className = 'cbm-offer-status';
      node.setAttribute('data-cbm-offer-status', '');
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      node.hidden = true;
      selector.append(node);
    }
    if (!node) return;
    // Existing template status nodes are made equivalent to the fallback native node. This keeps
    // the brief pending state available to assistive technology without relying on a visible label.
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.setAttribute('aria-atomic', 'true');
    node.textContent = message || '';
    node.hidden = !message;
    if (state) node.dataset.cbmOfferState = state;
    else delete node.dataset.cbmOfferState;
  }

  function refreshSelectorForForm(form) {
    const selector = selectorForForm(form);
    if (!selector) return;
    prepareNativeSelector(selector);

    let data;
    try {
      data = formDataFor(form);
    } catch (error) {
      selector.dataset.cbmOfferState = 'form-data-unavailable';
      return;
    }

    const variantId = selectedVariantId(form, data);
    const entry = mapForVariant(variantId);
    if (!entry) {
      selector.dataset.cbmOfferState = 'variant-unmapped';
      selector.hidden = true;
      return;
    }

    const prices = offerPriceSet(form, entry, variantId);
    if (!prices) {
      selector.dataset.cbmOfferState = 'market-data-unavailable';
      selector.hidden = true;
      return;
    }

    selector.hidden = false;
    selector.dataset.cbmCurrency = prices.currency;
    const canApplyGet3 = prices.canApplyAutomatic;
    selector.dataset.cbmOfferState = canApplyGet3 ? 'ready' : 'automatic-discount-unavailable';
    const activeQuantity = selectedQuantity(form, data);
    const activeSelection = activeSelectionPrice(form, data, entry);

    offerTiles(selector).forEach((tile) => {
      const tier = asPositiveInteger(tile.dataset.cbmOfferTier, 0);
      let cents = tier === 1 ? prices.q1 : tier === 2 ? prices.q2 : tier === 3 ? prices.q3 : null;
      if (tier === activeQuantity && activeSelection.selection.exact) cents = activeSelection.cents;
      const priceNode = priceNodeForTile(tile, selector);
      if (cents !== null && priceNode) priceNode.textContent = formatMoney(cents, prices.currency);
      if (cents === null && priceNode) priceNode.textContent = '';
      if (cents !== null) tile.dataset.cbmOfferCents = String(cents);
      else delete tile.dataset.cbmOfferCents;

      const active = tile.matches('input[type="radio"]') ? tile.checked : activeQuantity === tier;
      tile.classList.toggle('is-active', active);
      tile.setAttribute('aria-pressed', active ? 'true' : 'false');

      // Native automatic discount is verified; q1/q2 remain ordinary native prices.
      const unavailable = tier === 3 && !canApplyGet3;
      tile.classList.toggle('is-unavailable', unavailable);
      tile.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      if ('disabled' in tile) tile.disabled = unavailable;
    });

    if (activeQuantity > 1 && !activeSelection.selection.exact) {
      selector.dataset.cbmOfferState = 'variant-selection-incomplete';
      writeStatus(form, 'Please select an available option for every item.', 'variant-selection-incomplete');
    } else if (!canApplyGet3) {
      writeStatus(form, 'The automatic bundle offer is currently unavailable.', 'automatic-discount-unavailable');
    } else {
      writeStatus(form, 'Buy 3 or more items: 15% off all items, applied automatically in your cart.', 'automatic-ready');
    }
  }

  function quantityControlsFor(form) {
    return Array.from(document.querySelectorAll('input[name="quantity"]')).filter((input) => input.form === form);
  }

  function setTierQuantity(form, tier) {
    const quantity = asPositiveInteger(tier, 0);
    if (!quantity) return;
    const controls = quantityControlsFor(form);
    if (!controls.length) return;
    const radio = controls.find((input) => input.type === 'radio' && asPositiveInteger(input.value, 0) === quantity);
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      controls.forEach((input) => {
        input.value = String(quantity);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    refreshSelectorForForm(form);
  }

  function formForSelector(selector) {
    if (!selector) return null;
    if (selector.dataset.cbmFormId) return document.getElementById(selector.dataset.cbmFormId);
    if (selector.matches && selector.matches('quantity-breaks')) {
      const control = selector.querySelector('input[name="quantity"][form]');
      if (control && control.form) return control.form;
    }
    return selector.closest('form');
  }

  function wireSelector(selector) {
    if (!selector || selector.dataset.cbmOfferWired === 'true') return;
    selector.dataset.cbmOfferWired = 'true';
    selector.addEventListener('click', (event) => {
      const tile = event.target.closest('[data-cbm-offer-tier]');
      if (!tile || !selector.contains(tile) || tile.getAttribute('aria-disabled') === 'true') return;
      const form = formForSelector(selector);
      if (!form) return;
      if (selector.matches && selector.matches('quantity-breaks')) {
        // Keep Shrine in charge of radio and dropdown state. Its change event updates
        // selectedVariants; the document-level refresh below then reads that exact state.
        if (event.target.closest('.quantity-break__variants')) return;
        window.setTimeout(() => refreshSelectorForForm(form), 0);
        return;
      }
      event.preventDefault();
      setTierQuantity(form, tile.dataset.cbmOfferTier);
    });
  }

  function wireForm(form) {
    if (!form || wiredForms.has(form)) return;
    let formData;
    try {
      formData = formDataFor(form);
    } catch (error) {
      return;
    }
    if (!isImportedMappedForm(form, formData)) return;

    wiredForms.add(form);
    const selector = selectorForForm(form);
    wireSelector(selector);
    refreshSelectorForForm(form);

    // Variant pickers commonly mutate the hidden id value without firing an input event.
    // Observe only each imported form's id control; no global/subtree observer is used.
    if (window.MutationObserver) {
      Array.from(form.elements).filter((element) => element && element.name === 'id').forEach((input) => {
        new MutationObserver(() => window.setTimeout(() => refreshSelectorForForm(form), 0)).observe(input, {
          attributes: true,
          attributeFilter: ['value']
        });
      });
    }
  }

  function initialWire() {
    Array.from(document.querySelectorAll('form[action*="/cart/add"]')).forEach(wireForm);
  }

  function jsonHeaders() {
    return { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
  }

  async function requestJson(url, options) {
    const response = await window.fetch(url, options);
    let body = null;
    try {
      body = await response.json();
    } catch (error) {
      // The caller receives an actionable error rather than attempting to parse theme HTML.
    }
    if (!response.ok) {
      const message = body && (body.description || body.message) ? body.description || body.message : `Cart request failed (${response.status}).`;
      throw new Error(message);
    }
    return body || {};
  }

  function addPayloadFromFormData(data, sections, selectedVariantIds) {
    const item = { properties: {} };
    for (const [name, rawValue] of data.entries()) {
      if (typeof File !== 'undefined' && rawValue instanceof File && rawValue.name) return null;
      const value = String(rawValue);
      if (name === 'id') item.id = value;
      else if (name === 'quantity') item.quantity = asPositiveInteger(value, 1);
      else if (name === 'selling_plan' && value) item.selling_plan = value;
      else {
        const property = /^properties\[(.*)]$/.exec(name);
        if (property) item.properties[property[1]] = value;
      }
    }
    if (!item.id) return null;
    if (!Object.keys(item.properties).length) delete item.properties;

    const selected = Array.isArray(selectedVariantIds) && selectedVariantIds.length
      ? selectedVariantIds.map((id) => String(id)).filter(Boolean)
      : Array(item.quantity).fill(item.id);
    if (!selected.length) return null;

    // Shopify merges only identical line item attributes. Build the same request shape here so
    // duplicate per-unit selections (e.g. Black + Black) become one quantity-two line, while
    // Black + Grey remains two correct variant lines carrying the same custom properties.
    const grouped = new Map();
    const propertyKey = JSON.stringify(item.properties || {});
    selected.forEach((id) => {
      const key = `${id}\u0000${item.selling_plan || ''}\u0000${propertyKey}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.quantity += 1;
        return;
      }
      const next = { id, quantity: 1 };
      if (item.selling_plan) next.selling_plan = item.selling_plan;
      if (item.properties) next.properties = { ...item.properties };
      grouped.set(key, next);
    });

    const payload = { items: Array.from(grouped.values()) };
    if (sections.length) {
      payload.sections = sections;
      payload.sections_url = `${window.location.pathname}${window.location.search}`;
    }
    return payload;
  }

  function nativeDrawer() {
    return document.querySelector('cart-drawer');
  }

  function nativeCartPage() {
    return Array.from(document.querySelectorAll('cart-items')).find((component) => !component.closest('cart-drawer')) || null;
  }

  function sectionRequestId(section) {
    const id = typeof section === 'string' ? section : section && (section.section || section.id);
    return typeof id === 'string' && id.trim() ? id.trim() : '';
  }

  function publicSections(component) {
    if (!component || typeof component.getSectionsToRender !== 'function') return [];
    try {
      const sections = component.getSectionsToRender();
      return Array.isArray(sections) ? sections.filter((section) => sectionRequestId(section)) : [];
    } catch (error) {
      return [];
    }
  }

  function nativeSectionIds() {
    const ids = [];
    const drawerSections = publicSections(nativeDrawer());
    const pageSections = publicSections(nativeCartPage());
    drawerSections.concat(pageSections).forEach((section) => ids.push(sectionRequestId(section)));

    const declared = document.querySelector('[data-cbm-cart-sections]');
    if (!drawerSections.length && declared && declared.dataset.cbmCartSections) {
      declared.dataset.cbmCartSections.split(',').map((id) => id.trim()).filter(Boolean).forEach((id) => ids.push(id));
    }
    // Verified in the captured CartDiscountBlock native cart refresh contract.
    if (!ids.length) ids.push('cart-drawer', 'cart-icon-bubble');
    // The captured page has four regions; its drawer adds one unique region after the shared
    // cart-icon-bubble is deduplicated. Keep a custom-theme response bounded.
    return Array.from(new Set(ids)).slice(0, 5);
  }

  function renderAndOpenNativeDrawer(response) {
    const drawer = nativeDrawer();
    if (!drawer) return false;
    if (typeof drawer.renderContents === 'function') drawer.renderContents(response);
    drawer.classList.remove('is-empty');
    if (typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  function uniqueCodes(codes) {
    return Array.from(new Set(codes.map((code) => String(code || '').trim()).filter(Boolean)));
  }

  function stringsFromDiscountCodes(value) {
    if (typeof value === 'string') return value.split(',').map((code) => code.trim()).filter(Boolean);
    if (!Array.isArray(value)) return [];
    return value.flatMap((entry) => {
      if (typeof entry === 'string') return [entry.trim()];
      if (entry && typeof entry.code === 'string') return [entry.code.trim()];
      return [];
    }).filter(Boolean);
  }

  function knownCurrentCodes(cart) {
    if (cart && own(cart, 'discount_codes')) return { known: true, codes: uniqueCodes(stringsFromDiscountCodes(cart.discount_codes)) };
    if (cart && cart.attributes && own(cart.attributes, 'discount_codes')) {
      return { known: true, codes: uniqueCodes(stringsFromDiscountCodes(cart.attributes.discount_codes)) };
    }
    const nativeDiscountBlock = document.querySelector('cart-discount-block');
    if (nativeDiscountBlock) {
      const declared = nativeDiscountBlock.dataset.appliedCodes;
      const pills = Array.from(nativeDiscountBlock.querySelectorAll('.cart-discount__pill'))
        .map((pill) => pill.dataset.discountCode)
        .filter(Boolean);
      if (declared || pills.length) return { known: true, codes: uniqueCodes(declared ? stringsFromDiscountCodes(declared) : pills) };
    }
    const marker = document.querySelector('[data-cbm-discount-codes]');
    if (marker && marker.dataset.cbmDiscountCodes != null) {
      return { known: true, codes: uniqueCodes(stringsFromDiscountCodes(marker.dataset.cbmDiscountCodes)) };
    }
    return { known: false, codes: [] };
  }

  function eligibleMigrationCodes() {
    // Legacy code discounts are deactivated. Native automatic discounts require no code.
    return [];
  }

  function sameCodes(left, right) {
    const a = uniqueCodes(left).sort();
    const b = uniqueCodes(right).sort();
    return a.length === b.length && a.every((value, index) => value === b[index]);
  }

  function managedMigrationCode(code) {
    const normalized = String(code || '').toUpperCase();
    return normalized.startsWith(MIGRATION_CODE_PREFIX) || normalized === BUNDLE_SHIPPING_CODE;
  }



  function discountUpdatePlan(cart, currency) {
    const current = knownCurrentCodes(cart);
    const nonMigrationCodes = current.codes.filter((code) => !managedMigrationCode(code));
    const desiredCodes = uniqueCodes(nonMigrationCodes);
    if (!current.known) return { safe: false, codes: [], needsUpdate: false };
    return { safe: true, codes: desiredCodes, needsUpdate: !sameCodes(current.codes, desiredCodes) };
  }





  async function postDiscountCodes(codes, sections) {
    return requestJson(cartUrl('cart/update.js'), {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({
        updates: {},
        discount: codes.join(','),
        sections,
        sections_url: `${window.location.pathname}${window.location.search}`
      })
    });
  }

  async function reconcileDiscounts(cart, currency, sections) {
    const plan = discountUpdatePlan(cart, currency);
    if (!plan.safe || !plan.needsUpdate) return { plan, response: null, confirmed: true };
    // Only remove obsolete managed codes, preserving each unrelated shopper coupon. No
    // discount write is made on a clean cart; Shopify calculates automatic discounts itself.
    const response = await postDiscountCodes(plan.codes, sections);
    return { plan, response, confirmed: true };
  }





  async function submitOfferForm(form, data) {
    const sections = nativeSectionIds();
    const selected = selectedVariantIdsForForm(form, data);
    const quantity = selectedQuantity(form, data);
    if (!selected.exact || selected.ids.length !== quantity || !mapEntriesForVariantIds(selected.ids)) {
      writeStatus(form, 'Please select an available option for every item before adding to cart.', 'variant-selection-incomplete');
      return;
    }
    const payload = addPayloadFromFormData(data, sections, selected.ids);
    if (!payload) {
      writeStatus(form, 'This product option uses an unsupported attachment. Please use the native add-to-cart button.', 'native-form-required');
      return;
    }

    // The form/button pending hooks let the theme show a compact spinner if desired. Keep the
    // announcement short and avoid replacing the native button's own translated label or markup.
    writeStatus(form, 'Updating cart…', 'loading');
    const added = await requestJson(cartUrl('cart/add.js'), {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(payload)
    });

    const cart = await requestJson(cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
    const reconciliation = await reconcileDiscounts(cart, cartCurrency(cart), sections);
    const plan = reconciliation.plan;
    const finalResponse = reconciliation.response || added;


    const pageRendered = renderNativePage(finalResponse);
    const opened = renderAndOpenNativeDrawer(finalResponse);
    refreshSelectorForForm(form);
    if (!opened && !pageRendered) writeStatus(form, 'Cart updated.', 'drawer-unavailable');
    document.dispatchEvent(new CustomEvent('cbm:offer-cart-complete', { detail: { cart, response: finalResponse, plan } }));
  }

  function isSubmitControl(control) {
    if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) return false;
    const type = String(control.getAttribute('type') || (control instanceof HTMLButtonElement ? 'submit' : control.type || '')).toLowerCase();
    return type === 'submit' || type === 'image';
  }

  function nativeSubmitControl(form, submitter) {
    if (isSubmitControl(submitter) && submitter.form === form) return submitter;
    const controls = Array.from(form.elements || []).filter((control) => isSubmitControl(control));
    // Enter-key submission has no event.submitter in some supported theme/browser combinations.
    // form.elements includes controls linked with form="...", unlike a descendant-only query.
    return controls.find((control) => !control.disabled) || controls[0] || null;
  }

  function restoreAttribute(element, name, value) {
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  }

  function setFormPending(form, submitter) {
    if (!form || pendingFormStates.has(form)) return;
    const control = nativeSubmitControl(form, submitter);
    const state = {
      formAriaBusy: form.getAttribute('aria-busy'),
      formPending: form.getAttribute('data-cbm-offer-cart-pending'),
      control,
      controlDisabled: control ? control.disabled : false,
      controlAriaBusy: control ? control.getAttribute('aria-busy') : null,
      controlPending: control ? control.getAttribute('data-cbm-offer-cart-pending') : null
    };
    pendingFormStates.set(form, state);
    form.dataset.cbmOfferCartPending = 'true';
    form.setAttribute('aria-busy', 'true');
    if (!control) return;

    // Do not swap textContent/innerHTML: native add buttons may own translated labels and spinners.
    // Disabled state, accessibility state, and its original label are all restored after success or
    // error, while the capture-phase busy guard blocks every duplicate form submit.
    control.dataset.cbmOfferCartPending = 'true';
    control.setAttribute('aria-busy', 'true');
    control.disabled = true;
  }

  function clearFormPending(form) {
    const state = pendingFormStates.get(form);
    if (!state) return;
    restoreAttribute(form, 'aria-busy', state.formAriaBusy);
    restoreAttribute(form, 'data-cbm-offer-cart-pending', state.formPending);
    if (state.control) {
      state.control.disabled = state.controlDisabled;
      restoreAttribute(state.control, 'aria-busy', state.controlAriaBusy);
      restoreAttribute(state.control, 'data-cbm-offer-cart-pending', state.controlPending);
    }
    pendingFormStates.delete(form);
  }

  async function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || typeof window.fetch !== 'function') return;
    if (busyForms.has(form)) {
      // The native Shrine listener may otherwise receive this rapid second submission and add the
      // same mapped offer independently while the first owned transaction is still in flight.
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    let data;
    try {
      data = formDataFor(form);
    } catch (error) {
      return;
    }
    if (!isImportedMappedForm(form, data) || !form.checkValidity()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    busyForms.add(form);
    form.dataset.cbmOfferCartBusy = 'true';
    setFormPending(form, event.submitter);
    try {
      await queueCartTransaction('offer-add', () => submitOfferForm(form, data));
    } catch (error) {
      writeStatus(form, error && error.message ? error.message : 'Unable to update the cart. Please try again.', 'error');
    } finally {
      clearFormPending(form);
      busyForms.delete(form);
      delete form.dataset.cbmOfferCartBusy;
    }
  }

  function documentCurrency() {
    const support = document.querySelector('[data-cbm-currency]');
    return support && typeof support.dataset.cbmCurrency === 'string' && support.dataset.cbmCurrency.trim()
      ? support.dataset.cbmCurrency.toUpperCase()
      : USD;
  }

  function cartCurrency(cart) {
    return cart && typeof cart.currency === 'string' && cart.currency.trim()
      ? cart.currency.toUpperCase()
      : documentCurrency();
  }

  function renderNativePage(response) {
    const page = nativeCartPage();
    if (!page || !response || !response.sections || typeof page.getSectionInnerHTML !== 'function') return false;

    let rendered = false;
    publicSections(page).forEach((section) => {
      const responseId = sectionRequestId(section);
      const targetId = typeof section === 'object' && section ? section.id : '';
      const html = response.sections[responseId];
      if (!responseId || typeof targetId !== 'string' || !targetId || typeof html !== 'string') return;

      const target = document.getElementById(targetId);
      if (!target) return;
      try {
        // The page component owns this parser and its selector semantics. The bridge only puts
        // the returned native region back into the matching target.
        const innerHTML = page.getSectionInnerHTML(html, section.selector);
        if (typeof innerHTML !== 'string') return;
        const selected = typeof section.selector === 'string' ? target.querySelector(section.selector) : null;
        (selected || target).innerHTML = innerHTML;
        rendered = true;
      } catch (error) {
        // A missing custom-theme target is non-fatal; native cart handling remains authoritative.
      }
    });
    return rendered;
  }

  function renderNativeDrawer(response) {
    const drawer = nativeDrawer();
    if (!drawer || !response || !response.sections || typeof drawer.getSectionInnerHTML !== 'function') return false;

    // renderContents() is intentionally not used for a background page reconciliation: this
    // theme's public drawer implementation opens itself after rendering. Reuse its documented
    // section list/parser so the hidden drawer has current native markup without changing its
    // open state or focus.
    let rendered = false;
    publicSections(drawer).forEach((section) => {
      const responseId = sectionRequestId(section);
      const targetId = typeof section === 'object' && section ? section.id : '';
      const html = response.sections[responseId];
      if (!responseId || typeof html !== 'string') return;

      const target = typeof section.selector === 'string' && section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(targetId);
      if (!target) return;
      try {
        const innerHTML = drawer.getSectionInnerHTML(html, section.selector);
        if (typeof innerHTML !== 'string') return;
        target.innerHTML = innerHTML;
        rendered = true;
      } catch (error) {
        // A custom-theme drawer section can be absent; the cart page remains native-authoritative.
      }
    });
    if (rendered && Number.isInteger(response.item_count)) drawer.classList.toggle('is-empty', response.item_count === 0);
    return rendered;
  }

  async function reconcileCurrentCartNow() {
    if (typeof window.fetch !== 'function') return null;
    const cart = await requestJson(cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
    const reconciliation = await reconcileDiscounts(cart, cartCurrency(cart), nativeSectionIds());
    if (reconciliation.response) {
      renderNativePage(reconciliation.response);
      renderNativeDrawer(reconciliation.response);
    }
    return { cart, reconciliation };
  }

  function reconcileCurrentCart() {
    return queueCartTransaction('reconcile', reconcileCurrentCartNow);
  }

  function exactMappedLine(cart, expected) {
    if (!cart || !Array.isArray(cart.items) || !expected || !expected.variantId) return null;
    const indexed = Number.isInteger(expected.index) && expected.index > 0 ? cart.items[expected.index - 1] : null;
    const keyed = expected.lineKey
      ? cart.items.find((item) => String(item && item.key || '') === expected.lineKey)
      : null;
    const candidate = keyed || indexed;
    if (!candidate || !candidate.key) return null;
    if (expected.lineKey && String(candidate.key) !== expected.lineKey) return null;
    if (String(candidate.variant_id || candidate.id || '') !== expected.variantId) return null;
    return mapForVariant(candidate.variant_id || candidate.id) ? candidate : null;
  }

  function cartLineKey(element) {
    let current = element instanceof Element ? element : null;
    while (current) {
      const key = current.dataset && (current.dataset.cartItemKey || current.dataset.lineKey);
      if (typeof key === 'string' && key) return key;
      current = current.parentElement;
    }
    return '';
  }

  function closestQuantityInput(element) {
    if (element instanceof HTMLInputElement && element.name === 'updates[]') return element;
    let current = element instanceof Element ? element : null;
    while (current) {
      const input = current.querySelector && current.querySelector('input[name="updates[]"]');
      if (input instanceof HTMLInputElement) return input;
      current = current.parentElement;
    }
    return null;
  }

  function cartLineEvidence(element, quantity) {
    const input = closestQuantityInput(element);
    const value = quantity == null && input ? input.value : quantity;
    return {
      quantity: value,
      index: Number.parseInt((input && input.dataset.index) || (element && element.dataset && element.dataset.index) || '', 10),
      variantId: String((input && input.dataset.quantityVariantId) || (element && element.dataset && element.dataset.quantityVariantId) || ''),
      lineKey: cartLineKey(element)
    };
  }

  function belongsToNativeCart(element) {
    if (!(element instanceof Element)) return false;
    if (element.closest('cart-drawer')) return true;
    const page = nativeCartPage();
    return Boolean(page && page.contains(element));
  }

  function removeLineKey(removeButton) {
    const link = removeButton.querySelector('a[href]');
    if (!link) return '';
    try {
      const id = new URL(link.getAttribute('href'), window.location.href).searchParams.get('id') || '';
      return id.includes(':') ? id : '';
    } catch (error) {
      return '';
    }
  }

  function scopedNativeCartEvidence(element, quantity) {
    if (!belongsToNativeCart(element)) return null;
    const expected = cartLineEvidence(element, quantity);
    const requestedQuantity = Number.parseInt(String(expected.quantity == null ? '' : expected.quantity), 10);
    if (!Number.isInteger(requestedQuantity) || requestedQuantity < 0 || !expected.variantId || !mapForVariant(expected.variantId)) return null;
    expected.quantity = requestedQuantity;
    return expected;
  }

  async function postNativeLineChange(lineKey, quantity, sections) {
    return requestJson(cartUrl('cart/change.js'), {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({
        id: lineKey,
        quantity,
        sections,
        sections_url: `${window.location.pathname}${window.location.search}`
      })
    });
  }

  async function performOwnedNativeMutation(expected) {
    // Resolve the DOM index against a fresh cart while we own the FIFO. The request itself is
    // always keyed by Shopify's opaque line key, never by variant ID or a broad updates object;
    // duplicate variants with different properties therefore remain distinct.
    const before = await requestJson(cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
    const line = exactMappedLine(before, expected);
    if (!line) return null;

    const sections = nativeSectionIds();
    const changed = await postNativeLineChange(String(line.key), expected.quantity, sections);
    // A discount plan is deliberately computed only after the exact-line mutation has completed.
    // This is the one owner of both the quantity/remove write and the migration-code update.
    const cart = await requestJson(cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
    const reconciliation = await reconcileDiscounts(cart, cartCurrency(cart), sections);
    const response = reconciliation.response || changed;
    if (response) {
      // Both helpers use the Shrine component's section parser and never call drawer.open().
      // Thus a page action leaves its closed drawer closed; an active drawer remains active.
      renderNativePage(response);
      renderNativeDrawer(response);
    }
    return { cart, changed, reconciliation, lineKey: String(line.key) };
  }

  function queueOwnedNativeMutation(expected) {
    queueCartTransaction('native-line-mutation', () => performOwnedNativeMutation(expected)).catch(() => {
      // The scoped native control remains visible and can be retried by the shopper. Do not
      // replay a second cart/change request or fall through to a stale native AJAX handler.
    });
  }

  function formFromEventTarget(target) {
    if (!(target instanceof Element)) return null;
    if (target instanceof HTMLFormElement) return target;
    if (target.form instanceof HTMLFormElement) return target.form;
    return target.closest('form[action*="/cart/add"]');
  }

  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.name === 'updates[]') {
      const expected = scopedNativeCartEvidence(input);
      if (expected) {
        // Capture before Shrine's bubbling cart-items listener can submit its own /cart/change.js.
        // Only a mapped imported line is stopped; every other native cart row remains untouched.
        event.preventDefault();
        event.stopImmediatePropagation();
        queueOwnedNativeMutation(expected);
        return;
      }
    }
    const quantityBreaks = input instanceof Element ? input.closest('quantity-breaks') : null;
    const form = formFromEventTarget(event.target) || formForSelector(quantityBreaks);
    if (form) window.setTimeout(() => { wireForm(form); refreshSelectorForForm(form); }, 0);
  }, true);
  document.addEventListener('input', (event) => {
    const target = event.target;
    const quantityBreaks = target instanceof Element ? target.closest('quantity-breaks') : null;
    const form = formFromEventTarget(target) || formForSelector(quantityBreaks);
    if (form && event.target && event.target.name === 'id') window.setTimeout(() => refreshSelectorForForm(form), 0);
  }, true);
  document.addEventListener('click', (event) => {
    const target = event.target;
    const removeButton = target instanceof Element ? target.closest('cart-remove-button') : null;
    if (!removeButton) return;
    const expected = scopedNativeCartEvidence(removeButton, 0);
    if (!expected) return;
    // The page remove href contains a key where available; validate it against the fresh cart
    // inside the queued transaction rather than using a variant-level remove.
    const lineKey = removeLineKey(removeButton);
    if (lineKey) expected.lineKey = lineKey;
    event.preventDefault();
    event.stopImmediatePropagation();
    queueOwnedNativeMutation(expected);
  }, true);
  document.addEventListener('variant:change', (event) => {
    const form = formFromEventTarget(event.target);
    if (form) window.setTimeout(() => refreshSelectorForForm(form), 0);
  }, true);

  function initialCartPageReconciliation() {
    window.setTimeout(() => {
      reconcileCurrentCart().catch(() => {
        // Only a known code list can produce an update; leave initial native rendering untouched.
      });
    }, 0);
  }

  function initialize() {
    initialWire();
    initialCartPageReconciliation();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();

  window.CBMOfferCart = {
    version: '2.0.0',
    refresh: initialWire,
    mapForVariant,
    eligibleMigrationCodes,
    discountUpdatePlan,
    reconcileCurrentCart
  };
})();
